import { usePracticeScope } from '../../../../onboarding/practice/practiceScope';
import { utilizationWalkthroughProductKey } from '../../../../onboarding/utilizationValues';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UtensilsCrossed, ArrowLeftRight, ChevronDown, Download, Loader2, MoreVertical, Sparkles } from 'lucide-react';
import { TableComponent } from '../../../../ui/TableComponent';
import { FavoriteStar } from '../../../../ui/FavoriteStar';
import { TabbedSections } from '../../../../ui/TabbedSections';
import {
  UtilizationProductRow,
  UtilizationStatus,
  UtilizationTab,
  getUtilizationStatus,
  UtilizationGroupRow,
  UtilizationGroupMember,
} from '../../../../../utils/reports/utilizationReport';
import OverOrderingChart from './OverOrderingChart';
import { useChartAlignment } from './useChartAlignment';
import { darkGreenColor } from '../../../../../styling/stylingHelper';
import { Platform } from '../../../../../types/Platforms';
import { SupplierLogo as SharedSupplierLogo } from '../../../../ui/SupplierLogo';
import { eur } from '../../../../orderhistory/utils';
import { fmtBase } from '../../../../../utils/reports/shared';
import { getCategoryStyles } from '../../../../orderhistory/categoryUtils';
import { formatCategoryName } from '../../../../orderhistory/sections/FilterControls';
import { buildUtilizationCsv, buildUtilizationGroupedCsv, downloadCsv } from '../../../../../utils/reports/utilizationCsv';
import { cn } from '../../../../../lib/utils';
import { pctAbs } from '../common';
import { useAppSettingsStore } from '../../../../../stores/appSettingsStore';
import { ALL_RESTAURANTS_ID } from '../../../../../stores/appUserDefaultStore';
import { WasteActionPeriod } from '../../../../../utils/analysis/wasteActionPayload';
import { fmtBaseQty } from '../../../analysis/shared/analysisUnits';
import { useWasteActionRun } from './useWasteActionRun';

const STATUS_HEX: Record<UtilizationStatus, { text: string; bar: string }> = {
  healthy: { text: '#027A48', bar: '#12B76A' },
  review: { text: '#B54708', bar: '#F79009' },
  critical: { text: '#B42318', bar: '#F04438' },
};

interface OrderedVsNeededTableProps {
  /** Rows already scoped by the page's tab + shared filters. */
  rows: UtilizationProductRow[];
  /** Dish-grouped-tab groups (same tab + filter scoping as `rows`). */
  grouped: UtilizationGroupRow[];
  /** Pooled-tab groups (transitive alternative pools, same scoping). */
  pooled: UtilizationGroupRow[];
  /** Starred product keys (restaurant-wide, from report settings). */
  favoriteKeys: string[];
  onToggleFavorite: (key: string) => void;
  favoriteDisabled?: (key: string) => boolean;
  onRowClick: (row: UtilizationProductRow) => void;
  /** The page's tab — decides what the left-hand chart's bars measure. */
  chartTab: UtilizationTab;
  canSuggestWasteActionItems?: boolean;
  restaurantId?: string;
  fullRows?: UtilizationProductRow[];
  period?: WasteActionPeriod;
  wastePercentage?: number;
}

/** How the rows are SHAPED. What they are filtered to is the page's job. */
type ViewTab = 'products' | 'dishGrouped' | 'pooled';

/** Display row of a grouped tab: a group row, or — when the group is expanded —
 *  one of its member products rendered in the SAME columns for alignment. */
type GroupedTableRow =
  | { kind: 'group'; group: UtilizationGroupRow }
  | { kind: 'member'; member: UtilizationGroupMember };

const sortValue = (r: UtilizationProductRow, key: string): number => {
  switch (key) {
    case 'ordered': return r.orderedBase;
    case 'needed': return r.neededBase;
    case 'difference': return r.wasteBase;
    case 'utilization': return r.utilizationPercent ?? -Infinity;
    case 'wasteEur': return r.wasteEur ?? -Infinity;
    case 'usedIn': return r.dishes.length;
    default: return 0;
  }
};

const groupSortValue = (g: UtilizationGroupRow, key: string): number => {
  const v = (n: number | null) => (n === null ? -Infinity : n);
  switch (key) {
    case 'ordered': return v(g.orderedBase);
    case 'needed': return v(g.neededBase);
    case 'difference': return v(g.wasteBase);
    case 'utilization': return v(g.utilizationPercent);
    case 'wasteEur': return v(g.wasteEur);
    case 'usedIn': return g.dishes.length;
    default: return 0;
  }
};

const groupCategories = (g: UtilizationGroupRow): string[] =>
  Array.from(new Set(g.members.map((m) => m.category).filter((c): c is string => !!c)));

/** Utilization progress bar + % label, shared between all tabs. */
/** Percentage above its progress bar (the redesign's stacked utilization cell). */
const UtilizationCell: React.FC<{ pct: number | null }> = ({ pct }) => {
  if (pct === null) return <span className="text-muted dark:text-muted-dark">—</span>;
  const bar = STATUS_HEX[getUtilizationStatus(pct)].bar;
  return (
    <div className="flex flex-col gap-1.5 w-[76px]">
      <span className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark">{pctAbs(pct)}</span>
      <div className="w-full h-2 bg-black/10 dark:bg-white/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: bar }} />
      </div>
    </div>
  );
};

/** Ordered / needed base amount. Own span so the value carries an explicit
 *  colour — bare strings inherit the light-mode table colour in dark mode. */
const AmountCell: React.FC<{ base: number; stdUnit: string }> = ({ base, stdUnit }) => (
  <span className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark">{fmtBase(base, stdUnit)}</span>
);

/** Waste € with the base-unit surplus beneath it — the redesign folds the old
 *  separate "difference" column into this one. */
const WasteCell: React.FC<{ wasteEur: number | null; wasteBase: number; stdUnit: string }> = ({ wasteEur, wasteBase, stdUnit }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col">
      <span className={cn('text-sm font-semibold', wasteEur !== null && wasteEur > 0 ? 'text-textRed dark:text-textRed-dark' : 'text-textPrimary dark:text-textPrimary-dark')}>
        {wasteEur !== null ? eur(wasteEur) : t('reports.utilization.naValue')}
      </span>
      <span className="text-xs font-medium text-muted dark:text-muted-dark">
        {wasteBase > 0 ? '+' : ''}{fmtBase(wasteBase, stdUnit)}
      </span>
    </div>
  );
};

/** Article id · category badge · supplier logo — the product cell's second line.
 *  Category and supplier used to be their own columns. */
const ProductMetaLine: React.FC<{
  articleId?: string;
  category?: string;
  platform: string;
  unknownSupplierName?: string;
  supplier: string;
}> = ({ articleId, category, platform, unknownSupplierName, supplier }) => {
  const darkMode = useAppSettingsStore((s) => s.darkMode);
  return (
    <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
      {articleId && <span className="text-xs font-medium text-muted dark:text-muted-dark truncate">{articleId}</span>}
      {category && (
        <span className="text-[11px] font-medium rounded-md px-1.5 py-0.5 whitespace-nowrap" style={getCategoryStyles(category, darkMode)}>
          {formatCategoryName(category)}
        </span>
      )}
      <SupplierLogo platform={platform} unknownSupplierName={unknownSupplierName} supplier={supplier} />
    </div>
  );
};

const StatusBadge: React.FC<{ pct: number | null }> = ({ pct }) => {
  const { t } = useTranslation();
  const s = getUtilizationStatus(pct);
  const hex = STATUS_HEX[s].text;
  return (
    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap" style={{ backgroundColor: `${hex}1A`, color: hex }}>
      {t(`reports.utilization.status.${s}`)}
    </span>
  );
};

const SupplierLogo: React.FC<{ platform: string; unknownSupplierName?: string; supplier: string }> = ({ platform, unknownSupplierName, supplier }) => {
  const p = Platform.fromString(platform);
  return (
    <span title={supplier}>
      <SharedSupplierLogo platform={p} unknownSupplierName={unknownSupplierName} alt={supplier} size="small" />
    </span>
  );
};

/** A group's category, only when its members agree on one. */
const GroupCategoryBadge: React.FC<{ group: UtilizationGroupRow }> = ({ group }) => {
  const darkMode = useAppSettingsStore((s) => s.darkMode);
  const cats = groupCategories(group);
  if (cats.length !== 1) return null;
  return (
    <span className="text-[11px] font-medium rounded-md px-1.5 py-0.5 whitespace-nowrap" style={getCategoryStyles(cats[0], darkMode)}>
      {formatCategoryName(cats[0])}
    </span>
  );
};

/** Up to 3 distinct member suppliers, then a +N counter. */
const GroupSupplierLogos: React.FC<{ group: UtilizationGroupRow }> = ({ group }) => {
  const seen = new Set<string>();
  const distinct = group.members.filter((m) => {
    const id = `${m.platform}|${m.unknownSupplierName ?? ''}`;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return (
    <span className="flex items-center gap-1.5 shrink-0">
      {distinct.slice(0, 3).map((m) => (
        <SupplierLogo key={m.key} platform={m.platform} unknownSupplierName={m.unknownSupplierName} supplier={m.supplier} />
      ))}
      {distinct.length > 3 && <span className="text-xs text-muted dark:text-muted-dark">+{distinct.length - 3}</span>}
    </span>
  );
};

/** Muted em-dash placeholder for cells that don't apply to a row. */
const Dash: React.FC<{ title?: string }> = ({ title }) => (
  <span title={title} className="text-muted dark:text-muted-dark">—</span>
);

/**
 * Ordered-vs-Needed table on the shared app `TableComponent`: per-column sorting,
 * CSV export and the app's concise pagination.
 *
 * WHAT it shows is decided upstream — `UtilizationControls` owns the page tab and the
 * shared search/supplier/category/status/recipe filters, and this table receives rows
 * that are already scoped by them. Its own tabs only choose the SHAPE: Products (one
 * flat row per product), Dish grouped (GROUPS ONLY: each exact primary+alternatives set
 * as one expandable row, member ordered volumes sliced proportionally to the need
 * allocated from each set) and Grouped (GROUPS ONLY: transitive alternative pools with
 * full member numbers). Expanding a group inserts its member products as ordinary table
 * rows in the same columns, so member numbers align with (and sum to) the group row.
 */
export const OrderedVsNeededTable: React.FC<OrderedVsNeededTableProps> = ({
  rows,
  grouped,
  pooled,
  favoriteKeys,
  onToggleFavorite,
  favoriteDisabled,
  onRowClick,
  chartTab,
  canSuggestWasteActionItems = false, restaurantId, fullRows, period, wastePercentage = 0,
}) => {
  const { t } = useTranslation();
  const darkMode = useAppSettingsStore((s) => s.darkMode);
  const practice = usePracticeScope();
  const [viewTab, setViewTab] = useState<ViewTab>('products');
  const [expandedSigs, setExpandedSigs] = useState<Set<string>>(new Set());
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [mobileLimit, setMobileLimit] = useState(20);
  const wasteActionRun = useWasteActionRun();
  const menu = useRef<HTMLDivElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const allowed = canSuggestWasteActionItems && !practice && viewTab === 'products' && !!restaurantId && restaurantId !== ALL_RESTAURANTS_ID;
  const selecting = allowed && selectionMode;
  const selectedRows = rows.filter(row => selectedKeys.has(row.key));
  const label = (key: string, options?: Record<string, unknown>) => t(`analysis.wasteAutomation.${key}`, options);
  useEffect(() => {
    const visible = new Set(rows.map(row => row.key));
    setSelectedKeys(previous => {
      const next = new Set(Array.from(previous).filter(key => visible.has(key)));
      return next.size === previous.size ? previous : next;
    });
    setMobileLimit(20);
  }, [rows]);
  useEffect(() => {
    setMenuOpen(false); setSelectionMode(false); setSelectedKeys(new Set());
  }, [allowed, restaurantId]);
  useEffect(() => {
    if (!menuOpen) return;
    menu.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const outside = (event: MouseEvent) => { if (!menu.current?.contains(event.target as Node)) setMenuOpen(false); };
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setMenuOpen(false); menuButton.current?.focus(); }
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', keydown);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', keydown); };
  }, [menuOpen]);
  const toggleSelection = (key: string) => setSelectedKeys(previous => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key); else if (next.size < 10) next.add(key);
    return next;
  });

  const view = useMemo(() => {
    if (!sortColumn || !sortDirection) return rows;
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => (sortValue(a, sortColumn) - sortValue(b, sortColumn)) * dir);
  }, [rows, sortColumn, sortDirection]);

  const { wrapRef, onPageChange, sliceStart, sliceEnd, alignment } = useChartAlignment(
    `${viewTab}|${view.length}|${sortColumn}|${sortDirection}|${expandedSigs.size}`
  );

  // Which grouping feeds the group table: option sets (Dish grouped) or pools (Grouped).
  const activeGrouped = viewTab === 'pooled' ? pooled : grouped;

  // Sorted GROUP rows (also the CSV export source).
  const groupedGroups = useMemo<UtilizationGroupRow[]>(() => {
    if (!sortColumn || !sortDirection) return activeGrouped;
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...activeGrouped].sort((a, b) => (groupSortValue(a, sortColumn) - groupSortValue(b, sortColumn)) * dir);
  }, [activeGrouped, sortColumn, sortDirection]);

  // Display rows: expanded groups inject their members as ordinary rows right below.
  const groupedTableRows = useMemo<GroupedTableRow[]>(
    () =>
      groupedGroups.flatMap((g): GroupedTableRow[] =>
        expandedSigs.has(g.signature)
          ? [{ kind: 'group', group: g }, ...g.members.map((member): GroupedTableRow => ({ kind: 'member', member }))]
          : [{ kind: 'group', group: g }]
      ),
    [groupedGroups, expandedSigs]
  );

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc');
      if (sortDirection === 'desc') setSortColumn(null);
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const csvHeaders = [
    t('reports.utilization.col.product'), t('reports.utilization.csv.articleId'), t('reports.utilization.col.supplier'),
    t('reports.utilization.csv.category'), t('reports.utilization.col.dishes'), t('reports.utilization.col.ordered'),
    t('reports.utilization.col.needed'), t('reports.utilization.col.utilization'), t('reports.utilization.col.difference'),
    t('reports.utilization.col.wasteEur'), t('reports.utilization.col.status'),
    ...(rows.some(r => r.restaurantContributions) ? [t('reports.utilization.organization.localExcess'), t('reports.utilization.organization.localShortage')] : []),
  ];

  const handleExport = () => {
    if (viewTab === 'products') {
      downloadCsv(buildUtilizationCsv(view, csvHeaders), 'product-utilization.csv');
    } else {
      downloadCsv(
        buildUtilizationGroupedCsv(groupedGroups, csvHeaders.slice(0, 11)),
        viewTab === 'pooled' ? 'product-utilization-pooled.csv' : 'product-utilization-grouped.csv'
      );
    }
  };

  const toggleGroup = (signature: string) => {
    setExpandedSigs((prev) => {
      const next = new Set(prev);
      if (next.has(signature)) next.delete(signature);
      else next.add(signature);
      return next;
    });
  };

  const columns: any[] = [
    {
      key: 'name',
      label: t('reports.utilization.col.product'),
      render: (_v: any, r: UtilizationProductRow) => (
        <div className="flex items-center gap-2">
          {selecting && <input type="checkbox" aria-label={label('selectProduct', { name: r.name })} checked={selectedKeys.has(r.key)} disabled={!selectedKeys.has(r.key) && selectedRows.length >= 10} onClick={event => event.stopPropagation()} onChange={() => toggleSelection(r.key)} className="h-4 w-4 shrink-0" />}
          <span data-onboarding={practice ? "utilization-favorite" : undefined}><FavoriteStar disabled={favoriteDisabled?.(r.key)} isFavorite={favoriteKeys.includes(r.key)} onToggle={() => onToggleFavorite(r.key)} /></span>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark">{r.name}</span>
            <ProductMetaLine
              articleId={r.articleId}
              category={r.category}
              platform={r.platform}
              unknownSupplierName={r.unknownSupplierName}
              supplier={r.supplier}
            />
          </div>
        </div>
      ),
    },
    { key: 'ordered', label: t('reports.utilization.col.ordered'), sortable: true, sortKey: 'ordered', render: (_v: any, r: UtilizationProductRow) => <AmountCell base={r.orderedBase} stdUnit={r.stdUnit} /> },
    { key: 'needed', label: t('reports.utilization.col.needed'), sortable: true, sortKey: 'needed', render: (_v: any, r: UtilizationProductRow) => <AmountCell base={r.neededBase} stdUnit={r.stdUnit} /> },
    {
      key: 'utilization',
      label: t('reports.utilization.col.utilization'),
      sortable: true,
      sortKey: 'utilization',
      render: (_v: any, r: UtilizationProductRow) => <UtilizationCell pct={r.utilizationPercent} />,
    },
    {
      key: 'wasteEur',
      label: t('reports.utilization.col.wasteEur'),
      sortable: true,
      sortKey: 'wasteEur',
      render: (_v: any, r: UtilizationProductRow) => <WasteCell wasteEur={r.wasteEur} wasteBase={r.wasteBase} stdUnit={r.stdUnit} />,
    },
    {
      key: 'usedIn',
      label: t('reports.utilization.col.usedIn'),
      sortable: true,
      sortKey: 'usedIn',
      render: (_v: any, r: UtilizationProductRow) => (
        <span className="inline-flex items-center gap-1.5 bg-backgroundDarker dark:bg-backgroundDarker-dark rounded-md px-2 py-1 text-sm font-medium text-textPrimary dark:text-textPrimary-dark whitespace-nowrap">
          <UtensilsCrossed className="w-3.5 h-3.5 text-muted dark:text-muted-dark" />
          {t('reports.utilization.usedInDishes', { count: r.dishes.length })}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('reports.utilization.col.status'),
      render: (_v: any, r: UtilizationProductRow) => <StatusBadge pct={r.utilizationPercent} />,
    },
  ];

  // Grouped-tab columns: group rows and (when expanded) member rows share the exact
  // same columns, so member numbers align under the group aggregates they sum to.
  // Base-unit cells show — when a group mixes std units (€ still sums).
  const groupedColumns: any[] = [
    {
      key: 'name',
      label: t('reports.utilization.col.product'),
      render: (_v: any, it: GroupedTableRow) =>
        it.kind === 'group' ? (
          <div className="flex items-center gap-2">
            <ChevronDown className={cn('w-4 h-4 shrink-0 text-muted dark:text-muted-dark transition-transform', !expandedSigs.has(it.group.signature) && '-rotate-90')} />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark">{it.group.primaryName}</span>
              <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
                <span className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300">
                  <ArrowLeftRight className="w-3 h-3" />
                  {viewTab === 'pooled'
                    ? t('reports.utilization.poolBadge', { count: it.group.configuredCount })
                    : it.group.configuredCount - 1 === 1
                    ? t('reports.utilization.groupBadgeOne')
                    : t('reports.utilization.groupBadgeMany', { count: it.group.configuredCount - 1 })}
                </span>
                <GroupCategoryBadge group={it.group} />
                <GroupSupplierLogos group={it.group} />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col pl-8 min-w-0">
            <span className="text-sm font-medium text-textPrimary dark:text-textPrimary-dark">{it.member.name}</span>
            <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
              <span
                className={cn(
                  'inline-flex w-fit items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium',
                  it.member.isPrimary
                    ? 'bg-backgroundDarker dark:bg-backgroundDarker-dark text-textPrimary dark:text-textPrimary-dark'
                    : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300'
                )}
              >
                {it.member.isPrimary ? t('reports.utilization.groupPrimary') : t('reports.utilization.groupAlternative')}
              </span>
              {!it.member.row && (
                <span className="inline-flex w-fit items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-black/5 dark:bg-white/10 text-muted dark:text-muted-dark">
                  {t('reports.utilization.groupNotOrdered')}
                </span>
              )}
              {it.member.category && (
                <span className="text-[11px] font-medium rounded-md px-1.5 py-0.5 whitespace-nowrap" style={getCategoryStyles(it.member.category, darkMode)}>
                  {formatCategoryName(it.member.category)}
                </span>
              )}
              <SupplierLogo platform={it.member.platform} unknownSupplierName={it.member.unknownSupplierName} supplier={it.member.supplier} />
            </div>
          </div>
        ),
    },
    {
      key: 'ordered',
      label: t('reports.utilization.col.ordered'),
      sortable: true,
      sortKey: 'ordered',
      render: (_v: any, it: GroupedTableRow) =>
        it.kind === 'member'
          ? <AmountCell base={it.member.orderedBase} stdUnit={it.member.stdUnit} />
          : it.group.orderedBase !== null && it.group.stdUnit !== null
          ? <AmountCell base={it.group.orderedBase} stdUnit={it.group.stdUnit} />
          : <Dash title={t('reports.utilization.groupMixedUnits')} />,
    },
    {
      key: 'needed',
      label: t('reports.utilization.col.needed'),
      sortable: true,
      sortKey: 'needed',
      render: (_v: any, it: GroupedTableRow) =>
        it.kind === 'member'
          ? <AmountCell base={it.member.neededBase} stdUnit={it.member.stdUnit} />
          : it.group.neededBase !== null && it.group.stdUnit !== null
          ? <AmountCell base={it.group.neededBase} stdUnit={it.group.stdUnit} />
          : <Dash title={t('reports.utilization.groupMixedUnits')} />,
    },
    {
      key: 'utilization',
      label: t('reports.utilization.col.utilization'),
      sortable: true,
      sortKey: 'utilization',
      render: (_v: any, it: GroupedTableRow) => (
        <UtilizationCell pct={it.kind === 'member' ? it.member.utilizationPercent : it.group.utilizationPercent} />
      ),
    },
    {
      key: 'wasteEur',
      label: t('reports.utilization.col.wasteEur'),
      sortable: true,
      sortKey: 'wasteEur',
      render: (_v: any, it: GroupedTableRow) => {
        const wasteEur = it.kind === 'member' ? it.member.wasteEur : it.group.wasteEur;
        const wasteBase = it.kind === 'member' ? it.member.wasteBase : it.group.wasteBase;
        const stdUnit = it.kind === 'member' ? it.member.stdUnit : it.group.stdUnit;
        // A group mixing std units has no meaningful base-unit surplus — € still sums.
        if (wasteBase === null || stdUnit === null) {
          return (
            <div className="flex flex-col">
              <span className={cn('text-sm font-semibold', wasteEur !== null && wasteEur > 0 ? 'text-textRed dark:text-textRed-dark' : 'text-textPrimary dark:text-textPrimary-dark')}>
                {wasteEur !== null ? eur(wasteEur) : t('reports.utilization.naValue')}
              </span>
              <Dash title={t('reports.utilization.groupMixedUnits')} />
            </div>
          );
        }
        return <WasteCell wasteEur={wasteEur} wasteBase={wasteBase} stdUnit={stdUnit} />;
      },
    },
    {
      key: 'usedIn',
      label: t('reports.utilization.col.usedIn'),
      sortable: true,
      sortKey: 'usedIn',
      render: (_v: any, it: GroupedTableRow) =>
        it.kind === 'member' ? (
          <Dash />
        ) : (
          <span className="inline-flex items-center gap-1.5 bg-backgroundDarker dark:bg-backgroundDarker-dark rounded-md px-2 py-1 text-sm font-medium text-textPrimary dark:text-textPrimary-dark whitespace-nowrap">
            <UtensilsCrossed className="w-3.5 h-3.5 text-muted dark:text-muted-dark" />
            {t('reports.utilization.usedInDishes', { count: it.group.dishes.length })}
          </span>
        ),
    },
    {
      key: 'status',
      label: t('reports.utilization.col.status'),
      render: (_v: any, it: GroupedTableRow) =>
        it.kind === 'member' ? (
          it.member.utilizationPercent !== null ? <StatusBadge pct={it.member.utilizationPercent} /> : <Dash />
        ) : (
          <StatusBadge pct={it.group.utilizationPercent} />
        ),
    },
  ];

  const viewTabs = (
    <TabbedSections
      options={(['products', 'dishGrouped', 'pooled'] as ViewTab[]).map((tab) => ({
        value: tab,
        label: t(`reports.utilization.viewTab.${tab}`),
      }))}
      value={viewTab}
      onChange={(v) => setViewTab(v as ViewTab)}
      isTimeOptionSelection={false}
      className="w-full max-w-full lg:w-fit shrink-0"
    />
  );

  const exportButton = (
    <button
      type="button"
      onClick={handleExport}
      className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl border border-darkGreenColor dark:border-darkGreenColor-dark bg-background dark:bg-background-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark text-sm font-semibold whitespace-nowrap shrink-0"
      style={{ color: darkGreenColor }}
    >
      <Download className="w-5 h-5" />
      <span>{t('reports.utilization.exportCsv')}</span>
    </button>
  );

  // The chart mirrors the table only on the Products view — the grouped views' rows are
  // groups/members, which have no 1:1 product bar. There the chart stays standalone.
  const aligned = viewTab === 'products';
  const visibleRows = aligned ? view.slice(sliceStart, sliceEnd) : rows;

  return (
    <div className="h-full min-w-0 flex flex-col gap-3">
      {/* Title + view tabs + export live in their OWN card, so the table card below starts
          with its `thead` — that's what lets the chart's title band line up with it. */}
      <div className="rounded-xl bg-background dark:bg-background-dark shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040] p-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-4 min-w-0">
          <div className="min-w-0">
            <div className="text-lg font-semibold text-textPrimary dark:text-textPrimary-dark">
              {t('reports.utilization.table.title')}
            </div>
            {viewTab !== 'products' && (
              <p className="mt-1 text-xs text-muted dark:text-muted-dark">
                {activeGrouped.length === 0
                  ? t('reports.utilization.noGroups')
                  : viewTab === 'pooled'
                  ? t('reports.utilization.pooledExplainer')
                  : t('reports.utilization.groupedExplainer')}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center w-full min-w-0 lg:w-auto lg:shrink-0">
            {viewTabs}
            <div className="flex items-center gap-2">
              {exportButton}
              {allowed && !selecting && <div ref={menu} className="relative shrink-0">
                <button ref={menuButton} type="button" aria-label={t('products.actions.menuLabel')} aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(open => !open)}
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-borderGrey dark:border-borderGrey-dark bg-background dark:bg-background-dark text-textPrimary dark:text-textPrimary-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark">
                  <MoreVertical className="h-5 w-5" />
                </button>
                {menuOpen && <div role="menu" className="absolute right-0 top-full z-50 mt-1 w-64 max-w-[80vw] rounded-xl border border-borderGrey dark:border-borderGrey-dark bg-background dark:bg-background-dark py-1 shadow-lg">
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setSelectionMode(true); }} className="flex min-h-11 w-full items-center gap-2 px-4 py-2 text-left text-sm text-textPrimary dark:text-textPrimary-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark">
                    <Sparkles className="h-4 w-4 shrink-0" />{label('suggest')}
                  </button>
                </div>}
              </div>}
            </div>
          </div>
        </div>
      </div>

      {selecting && <div className="flex flex-wrap items-center gap-3 rounded-xl bg-background dark:bg-background-dark p-3">
        <span role="status" className="text-sm text-textPrimary dark:text-textPrimary-dark">{label('selected', { count: selectedRows.length })}</span>
        <button type="button" disabled={wasteActionRun.running} onClick={() => { setSelectionMode(false); setSelectedKeys(new Set()); }} className="min-h-11 text-sm text-muted dark:text-muted-dark disabled:opacity-50">{label('cancel')}</button>
        <button type="button" disabled={wasteActionRun.running || !selectedRows.length || !fullRows || !period} onClick={() => {
          if (restaurantId && fullRows && period) void wasteActionRun.run({ restaurantId, selectedKeys: selectedRows.map(row => row.key), rows: fullRows, period: { ...period }, wastePercentage });
        }} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-darkGreenColor dark:bg-darkGreenColor-dark px-4 py-2 text-sm font-semibold text-white dark:text-white disabled:opacity-50">
          {wasteActionRun.running && <Loader2 className="h-4 w-4 animate-spin" />}
          {label(wasteActionRun.running ? 'running' : 'suggestCount', { count: selectedRows.length })}
        </button>
        {wasteActionRun.error && <span role="alert" className="text-sm text-red-700 dark:text-red-300">{label(`error.${wasteActionRun.error}`)}</span>}
      </div>}
      {selecting && <div className="space-y-2 sm:hidden">
        {view.slice(0, mobileLimit).map(row => <label key={row.key} className="flex min-h-11 items-start gap-3 rounded-xl bg-background dark:bg-background-dark p-4 text-textPrimary dark:text-textPrimary-dark">
          <input type="checkbox" className="mt-1 h-4 w-4 shrink-0" checked={selectedKeys.has(row.key)} disabled={!selectedKeys.has(row.key) && selectedRows.length >= 10} onChange={() => toggleSelection(row.key)} />
          <span className="min-w-0 flex-1"><span className="block font-semibold">{row.name}</span><span className="block text-xs text-muted dark:text-muted-dark">{row.supplier}</span>
            <span className="mt-2 grid grid-cols-2 gap-2 text-sm"><span>{t('reports.utilization.col.ordered')}: {fmtBaseQty(t, row.orderedBase, row.stdUnit)}</span><span>{t('reports.utilization.col.needed')}: {fmtBaseQty(t, row.neededBase, row.stdUnit)}</span></span>
            <span className="mt-2 block text-sm">{t('reports.utilization.col.wasteEur')}: {row.wasteEur === null ? '—' : eur(row.wasteEur)}</span>
          </span>
        </label>)}
        {mobileLimit < view.length && <button type="button" onClick={() => setMobileLimit(limit => limit + 20)} className="min-h-11 w-full text-sm text-darkGreenColor dark:text-darkGreenColor-dark">{label('showMore')}</button>}
      </div>}
      <div className={cn('flex flex-col-reverse gap-5 lg:grid lg:grid-cols-[minmax(280px,420px)_1fr] lg:items-stretch', selecting && 'hidden sm:flex')}>
        <OverOrderingChart
          rows={visibleRows}
          unmappedRows={[]}
          tab={chartTab}
          align={aligned ? alignment : undefined}
        />

        <div ref={wrapRef} className="min-w-0 w-full h-full flex flex-col">
          {viewTab === 'products' ? (
            <TableComponent<UtilizationProductRow>
              rowOnboardingId={r => practice && r.key === utilizationWalkthroughProductKey() ? "utilization-row" : undefined}
              title=""
              data={view}
              columns={columns}
              blankLines={false}
              usePagination
              alignOtherColumnsRight={false}
              onRowClick={onRowClick}
              onPageChange={onPageChange}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              className="flex-1 min-h-0"
            />
          ) : (
            <TableComponent<GroupedTableRow>
              title=""
              data={groupedTableRows}
              columns={groupedColumns}
              blankLines={false}
              usePagination
              alignOtherColumnsRight={false}
              onRowClick={(it) => {
                if (it.kind === 'group') toggleGroup(it.group.signature);
                else if (it.member.row) onRowClick(it.member.row);
              }}
              isRowClickable={(it) => it.kind === 'group' || !!it.member.row}
              onPageChange={onPageChange}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              className="flex-1 min-h-0"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderedVsNeededTable;
